import { useEffect, useId, useRef } from "react";
import type { SyntheticEvent } from "react";

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly confirmVariant?: "primary" | "danger";
  onConfirm(): void;
  onCancel(): void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  confirmVariant = "primary",
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const confirmClass =
    confirmVariant === "danger" ? "btn btn--danger" : "btn btn--primary";

  // Native <dialog> fires `cancel` on ESC. Forward to onCancel AND prevent the
  // default so the parent's `open` prop drives the close (keeps state in sync).
  const handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    event.preventDefault();
    onCancel();
  };

  return (
    <dialog
      ref={ref}
      className="confirm-dialog"
      role={confirmVariant === "danger" ? "alertdialog" : "dialog"}
      aria-labelledby={titleId}
      aria-describedby={messageId}
      onCancel={handleNativeCancel}
    >
      <div className="confirm-dialog__content">
        <h2 id={titleId} className="confirm-dialog__title">
          {title}
        </h2>
        <p id={messageId} className="confirm-dialog__message">
          {message}
        </p>
        <div className="confirm-dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={confirmClass} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
